// Card «Por qué estas condiciones» del Resumen: con qué plan y qué edición se cotizó, qué
// promociones y qué políticas especiales pisaron los términos. Todo sale de lo que el crédito
// congeló al originar; nada se deriva ni se recalcula aquí.
import type { ReactNode } from 'react';
import type { AppliedPromotionSnapshot } from '@fiado/type-kit/bin/loanCredit/dtos/AppliedPromotionSnapshot';
import type { LoanCreditResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanCreditResponse';
import {
    NO_APPLIED_PROMOTIONS_NOTE,
    NO_APPLIED_STORE_POLICIES_NOTE,
    NO_CANDIDATE_PLANS_NOTE,
    NO_EFFECTIVE_TERMS_NOTE,
    NO_PLAN_VERSION_NOTE,
} from '../gaps';
import { CLIENT_LEVEL_LABEL, formatMoneyCents } from '../logic';
import {
    foldableTermLabel,
    formatSharePct,
    NO_DOWN_PAYMENT_CAP_TEXT,
    planRationaleOf,
    promotionEffectLabel,
    promotionsBlockOf,
    storePoliciesBlockOf,
} from '../rationale';
import { CreditCard, CreditChip, CreditGapMark, CreditKeyValue } from './CreditFileUi';

/** Los chips de un renglón, alineados a la derecha dentro de la celda de `CreditKeyValue`. */
function ChipList({ children }: { children: ReactNode }) {
    return <span className="flex flex-wrap items-center justify-end gap-1">{children}</span>;
}

/** «Ninguna» es lo que AFIRMA el arreglo vacío del silo, no un dato que falte: va sin centinela. */
function NoneValue() {
    return <span className="font-normal text-muted-foreground">Ninguna</span>;
}

/** Chip corto cuyo detalle entero se lee al pasar el cursor y con lector de pantalla. */
function DetailedChip({ note, children }: { note: string; children: ReactNode }) {
    return (
        <span className="cursor-help" title={note}>
            <CreditChip>{children}</CreditChip>
            <span className="sr-only">{note}</span>
        </span>
    );
}

/** Rótulo que carga un dato de contexto sin gastarle un renglón propio a la card. */
function LabelWithNote({ text, note }: { text: string; note: string | null }) {
    if (note === null) return <>{text}</>;
    return (
        <span className="cursor-help" title={note}>
            {text}
            <span className="sr-only"> {note}</span>
        </span>
    );
}

/** Los segmentos viajan como ids y aquí no hay catálogo que los resuelva: se cuentan, no se pintan. */
function segmentsNoteOf(segmentIds: readonly string[] | null | undefined): string | null {
    if (segmentIds == null) return null;
    if (segmentIds.length === 0) return 'El cliente cotizó en el segmento Estándar.';
    const count = segmentIds.length;
    return `El cliente cotizó en ${String(count)} ${count === 1 ? 'segmento' : 'segmentos'}.`;
}

/** Una promoción se lee como su política hermana: nombre y qué término del plan movió. */
function promotionChipText(promotion: AppliedPromotionSnapshot): string {
    const effects = [...new Set(promotion.appliedEffects.map(promotionEffectLabel))].sort((a, b) =>
        a.localeCompare(b, 'es-MX'),
    );
    return effects.length === 0 ? promotion.name : `${promotion.name} · ${effects.join(', ')}`;
}

export interface CreditRationaleCardProps {
    credit: LoanCreditResponse;
}

export function CreditRationaleCard({ credit }: CreditRationaleCardProps) {
    const plan = planRationaleOf(credit);
    const promotions = promotionsBlockOf(credit);
    const policies = storePoliciesBlockOf(credit);
    const version = credit.planVersionAtOrigination;
    const level = credit.clientLevelAtOrigination;
    const terms = credit.effectiveTerms;
    const rivals = plan.rivals;

    return (
        <CreditCard
            title="Por qué estas condiciones"
            footnote="Ya están incluidas en los montos de arriba."
        >
            <CreditKeyValue
                label="Plan"
                value={
                    <ChipList>
                        <span>
                            {version == null ? plan.code : `${plan.code} · v${String(version)}`}
                        </span>
                        {level ? <CreditChip>{CLIENT_LEVEL_LABEL[level]}</CreditChip> : null}
                        {plan.presence === 'missing' ? (
                            <CreditGapMark note={NO_CANDIDATE_PLANS_NOTE} />
                        ) : rivals.length === 0 ? null : (
                            <DetailedChip note={`También compitieron: ${rivals.join(', ')}.`}>
                                +{String(rivals.length)} {rivals.length === 1 ? 'plan' : 'planes'}
                            </DetailedChip>
                        )}
                    </ChipList>
                }
                mark={
                    version == null ? (
                        <CreditGapMark note={NO_PLAN_VERSION_NOTE} label="sin edición" />
                    ) : null
                }
            />

            <CreditKeyValue
                label={
                    <LabelWithNote
                        text="Promociones"
                        note={segmentsNoteOf(credit.segmentIdsAtQuote)}
                    />
                }
                note={promotions.presence === 'missing' ? NO_APPLIED_PROMOTIONS_NOTE : undefined}
                value={
                    promotions.presence === 'empty' ? (
                        <NoneValue />
                    ) : (
                        <ChipList>
                            {promotions.items.map((item) => (
                                <CreditChip key={item.promotionId} tone="info">
                                    {promotionChipText(item)}
                                </CreditChip>
                            ))}
                        </ChipList>
                    )
                }
            />

            <CreditKeyValue
                label="Políticas"
                note={policies.presence === 'missing' ? NO_APPLIED_STORE_POLICIES_NOTE : undefined}
                value={
                    policies.presence === 'empty' ? (
                        <NoneValue />
                    ) : (
                        <ChipList>
                            {policies.items.map((item) => (
                                <CreditChip key={item.policyId} tone="warning">
                                    {item.name} ·{' '}
                                    {item.appliedTerms.map(foldableTermLabel).join(', ')}
                                </CreditChip>
                            ))}
                        </ChipList>
                    )
                }
            />

            {terms == null ? (
                // El hueco es del bloque entero, no de cada cota: una marca sola, no una por renglón.
                <CreditKeyValue label="Cotas al cotizar" note={NO_EFFECTIVE_TERMS_NOTE} />
            ) : (
                <>
                    <CreditKeyValue
                        label="Enganche"
                        value={`${formatSharePct(terms.minDownPaymentPct)} – ${
                            terms.maxDownPaymentPct === null
                                ? NO_DOWN_PAYMENT_CAP_TEXT
                                : formatSharePct(terms.maxDownPaymentPct)
                        }`}
                    />
                    <CreditKeyValue
                        label="Financiable hasta"
                        value={formatMoneyCents(terms.maxFinancedAmountCents)}
                    />
                </>
            )}
        </CreditCard>
    );
}
